/**
 * useActivityPeriodFilter Hook
 *
 * Manages period filter state for Activity page - same logic as Analytics.
 * Supports: All time, Daily, Weekly, Monthly, Yearly, Custom Period.
 * Default: Monthly (current month).
 * Arrows navigate between periods.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { setDateRangeByPeriod } from "../../../utils/dateRangeUtils";

export interface UseActivityPeriodFilterParams {
  workspaceId: string;
  startDate: string;
  endDate: string;
  setStartDate: (date: string) => void;
  setEndDate: (date: string) => void;
  workspaceCreatedAt: Date | null;
  initialFilters?: {
    startDate?: string;
    endDate?: string;
  };
}

export interface UseActivityPeriodFilterResult {
  selectedPeriod: string;
  setSelectedPeriod: (period: string) => void;
  currentPeriodOffset: number;
  handlePeriodChange: (period: string) => void;
  handlePeriodNavigation: (direction: "left" | "right") => void;
  setDateRangeByPeriodFn: (periodType: string, offset: number) => void;
}

export function useActivityPeriodFilter({
  workspaceId,
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  workspaceCreatedAt,
  initialFilters,
}: UseActivityPeriodFilterParams): UseActivityPeriodFilterResult {
  const [selectedPeriod, setSelectedPeriod] = useState<string>("Monthly");
  const [currentPeriodOffset, setCurrentPeriodOffset] = useState<number>(0);
  const hasInitializedRef = useRef<boolean>(false);

  const setDateRangeByPeriodFn = useCallback(
    (periodType: string, offset: number = 0) => {
      setDateRangeByPeriod(
        periodType,
        offset,
        setStartDate,
        setEndDate,
        workspaceCreatedAt
      );
    },
    [setStartDate, setEndDate, workspaceCreatedAt]
  );

  const handlePeriodChange = useCallback(
    (period: string) => {
      setSelectedPeriod(period);
      setCurrentPeriodOffset(0);
      if (period === "Custom") return;
      if (period === "All time") {
        setDateRangeByPeriodFn(period, 0);
        return;
      }
      setDateRangeByPeriodFn(period, 0);
    },
    [setDateRangeByPeriodFn]
  );

  const handlePeriodNavigation = useCallback(
    (direction: "left" | "right") => {
      if (!selectedPeriod || selectedPeriod === "All time") return;
      const newOffset =
        direction === "left" ? currentPeriodOffset - 1 : currentPeriodOffset + 1;
      setCurrentPeriodOffset(newOffset);
      setDateRangeByPeriodFn(selectedPeriod, newOffset);
    },
    [selectedPeriod, currentPeriodOffset, setDateRangeByPeriodFn]
  );

  // Initialize: default Monthly period on mount (same as Analytics)
  useEffect(() => {
    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;

      // If initialFilters has dates, use them (e.g. navigated from Analytics)
      if (initialFilters?.startDate || initialFilters?.endDate) {
        if (initialFilters.startDate) setStartDate(initialFilters.startDate);
        if (initialFilters.endDate) setEndDate(initialFilters.endDate);
        // Keep "Monthly" or infer period from dates - keep simple for now
        return;
      }

      // Set period to Monthly if not already set
      setSelectedPeriod("Monthly");
      setCurrentPeriodOffset(0);
      setDateRangeByPeriodFn("Monthly", 0);
    }
  }, [workspaceId]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    selectedPeriod,
    setSelectedPeriod,
    currentPeriodOffset,
    handlePeriodChange,
    handlePeriodNavigation,
    setDateRangeByPeriodFn,
  };
}
